import { Button } from "@/components/ui/Button";
import {
  getSelectionIconClassName,
  selectionCheckClassName,
  selectionRowClassName,
} from "@/components/ui/SelectionRowStyles";
import type { ClientEstate } from "@/types";
import { getWatchDogIcon } from "@/components/hlidaci-pes/watchdog-page/watchDogOverviewUtils";
import { useLanguage } from "@/context/LanguageContext";
import { CheckIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

type PortfolioSelectorButtonProps = {
  estate: ClientEstate | null;
  isActive: boolean;
  onClick: () => void;
};

export function PortfolioSelectorButton({
  estate,
  isActive,
  onClick,
}: PortfolioSelectorButtonProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      property: "Nemovitost",
      rented: "Pronajímáno",
      vacant: "Neobsazeno",
      ownHome: "Vlastní bydlení",
    },
    en: {
      property: "Property",
      rented: "Rented",
      vacant: "Vacant",
      ownHome: "Owner occupied",
    },
  }[language];
  const Icon = getWatchDogIcon(estate ? estate.estateType : "");
  const title = estate?.name ?? copy.property;
  const normalizedStatus = estate?.status?.toUpperCase() ?? "";
  const status = normalizedStatus.includes("RENTED")
    ? copy.rented
    : normalizedStatus.includes("VACANT")
      ? copy.vacant
      : copy.ownHome;
  const details = [status, estate?.flatCategory].filter(Boolean).join(" · ");

  return (
    <Button
      type="button"
      variant="unstyled"
      aria-pressed={isActive}
      data-active={isActive}
      onClick={onClick}
      className={selectionRowClassName}
    >
      <span className={getSelectionIconClassName(isActive)}>
        <Icon aria-hidden="true" className="size-4" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium">{title}</span>
        <span className="block truncate text-[10px] text-foreground/45">
          {details}
        </span>
      </span>

      {isActive ? (
        <span className={selectionCheckClassName}>
          <CheckIcon aria-hidden="true" className="size-4 stroke-[2.5]" />
        </span>
      ) : (
        <ChevronRightIcon
          aria-hidden="true"
          className="size-4 shrink-0 text-foreground/30 transition-transform group-hover:translate-x-0.5"
        />
      )}
    </Button>
  );
}
